import React, { useMemo, useEffect } from "react";
import { useSearchParams } from "react-router-dom";

import RedesignReviewPost from "../components/PostManager/ReviewPost";
import ConnectPage from "../components/Connect/ConnectPage";
import AnalyticsDashboard from "../features/analytics/components/AnalyticsDashboard";
import BlogsPage from "./BlogsPage";
import RedditPage from "./RedditPage";
import { useCustomerWorkspace } from "../context/CustomerWorkspaceContext";
import { useRedditFeed } from "../context/RedditFeedContext";

type MainTab = "review" | "blogs" | "reddit" | "connect" | "analytics";

// Social + Blogs + Reddit are the faces of the "Content" hub; Connections/
// Analytics remain their own destinations (reached from the sidebar) but
// share this page.
const CONTENT_TABS: { key: MainTab; label: string }[] = [
  { key: "review", label: "Social" },
  { key: "blogs", label: "Blogs" },
  { key: "reddit", label: "Reddit" },
];

const SocialPostsPage = () => {
  const [searchParams, setSearchParams] = useSearchParams();
  const { selectedCustomerName } = useCustomerWorkspace();
  // Read from the app-wide provider rather than the Reddit tab's own state, so
  // the indicator keeps ticking while the user sits on Social or Blogs — the
  // build is still running whether or not that tab is mounted.
  const redditFeed = useRedditFeed();
  const redditBusy = redditFeed.isLoading || redditFeed.isRefreshing;

  const selectedTab = useMemo((): MainTab => {
    const t = searchParams.get("tab");
    if (t === "connect") return "connect";
    if (t === "analytics") return "analytics";
    if (t === "blogs") return "blogs";
    if (t === "reddit") return "reddit";
    return "review";
  }, [searchParams]);

  useEffect(() => {
    if (searchParams.get("tab") === "create") {
      setSearchParams({ tab: "review" }, { replace: true });
    }
  }, [searchParams, setSearchParams]);

  const isContentHub =
    selectedTab === "review" || selectedTab === "blogs" || selectedTab === "reddit";

  const title =
    selectedTab === "connect"
      ? "Connections"
      : selectedTab === "analytics"
      ? "Analytics"
      : "Content";

  const selectContentTab = (tab: MainTab) => {
    const next = new URLSearchParams(searchParams);
    next.set("tab", tab);
    setSearchParams(next, { replace: true });
  };

  return (
    <div className="p-6">
      <div className="mx-auto max-w-[1130px] space-y-6">
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-2xl font-bold text-theme-fg">{title}</h1>
            {selectedTab !== "connect" && selectedCustomerName && (
              <div className="mt-2 inline-flex items-center gap-2 rounded-full bg-green-50 px-3 py-1.5 text-sm font-medium text-green-700 shadow-sm ring-1 ring-inset ring-green-600/20 transition-all hover:bg-green-100 dark:bg-green-900/20 dark:text-green-400 dark:ring-green-500/20 dark:hover:bg-green-900/40">
                <span className="material-icons-outlined text-[18px] text-amber-600 opacity-90 dark:text-amber-500">
                  storefront
                </span>
                <span>{selectedCustomerName}</span>
              </div>
            )}
          </div>
        </div>

        {isContentHub && (
          <div
            role="tablist"
            aria-label="Content type"
            className="inline-flex items-center gap-1 rounded-lg border border-theme-border bg-theme-surface p-1"
          >
            {CONTENT_TABS.map((tab) => {
              const active = selectedTab === tab.key;
              // A Reddit build runs 60-100s and deliberately survives the user
              // navigating away (the provider sits above the router). Without
              // a marker here, leaving the tab looks like the run was lost —
              // so the tab itself reports that it's still working.
              const showSpinner = tab.key === "reddit" && redditBusy;
              return (
                <button
                  key={tab.key}
                  type="button"
                  role="tab"
                  aria-selected={active}
                  onClick={() => selectContentTab(tab.key)}
                  title={showSpinner ? "Checking Reddit for new questions…" : undefined}
                  className={`flex items-center gap-1.5 rounded-md px-4 py-1.5 text-sm font-medium transition-colors ${
                    active
                      ? "bg-theme-secondary/15 text-theme-fg"
                      : "text-theme-fg-muted hover:bg-theme-secondary/10 hover:text-theme-fg"
                  }`}
                >
                  {tab.label}
                  {showSpinner && (
                    <span
                      aria-label="Checking Reddit for new questions"
                      role="status"
                      className="h-3 w-3 shrink-0 animate-spin rounded-full border-2 border-brand-primary/30 border-t-brand-primary"
                    />
                  )}
                </button>
              );
            })}
          </div>
        )}

        <div className="rounded-md border border-theme-border bg-theme-surface p-6 shadow-sm">
          {selectedTab === "review" && <RedesignReviewPost />}
          {selectedTab === "blogs" && <BlogsPage embedded />}
          {selectedTab === "reddit" && <RedditPage embedded />}
          {selectedTab === "connect" && <ConnectPage embedded />}
          {selectedTab === "analytics" && <AnalyticsDashboard />}
        </div>
      </div>
    </div>
  );
};

export default SocialPostsPage;
